import { keyframes, type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { type FC } from 'react';

export type SpinnerColor = 'secondary' | 'primary' | 'inherit';

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const SpinnerSvg = styled.svg`
  animation: ${spin} 0.8s linear infinite;
`;

const spinnerColor = (theme: Theme): Record<SpinnerColor, string> => ({
  primary: theme.colors.icon.primary,
  secondary: theme.colors.text.secondary,
  inherit: 'inherit',
});

const SpinnerWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== '$color',
})<{ $color: SpinnerColor }>(({ theme, $color }) => ({
  width: 16,
  height: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: spinnerColor(theme)[$color],
}));

/** Used in Buttons loading */
type SpinnerProps = {
  /** `inherit` takes the colour of the control it sits in, e.g. a loading button's label. */
  color?: SpinnerColor;
};

const Spinner: FC<SpinnerProps> = ({ color = 'secondary' }) => (
  <SpinnerWrapper $color={color}>
    <SpinnerSvg
      width="13"
      height="13"
      viewBox="0 0 13 13"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0 6.5C0 5.09847 0.453 3.73449 1.29145 2.61142C2.12991 1.48836 3.3089 0.666378 4.65263 0.268049C5.99636 -0.130281 7.43285 -0.0836218 8.74789 0.401068C10.0629 0.885758 11.1861 1.78251 11.9499 2.95762C12.7137 4.13273 13.0772 5.52324 12.9863 6.92181C12.8953 8.32038 12.3548 9.6521 11.4452 10.7184C10.5357 11.7847 9.30581 12.5284 7.93906 12.8387C6.57231 13.149 5.14188 13.0092 3.86106 12.4402L4.26633 11.5279C5.35045 12.0096 6.56121 12.1279 7.71806 11.8652C8.87491 11.6026 9.91589 10.9731 10.6858 10.0706C11.4556 9.16802 11.9132 8.04082 11.9902 6.85703C12.0672 5.67324 11.7595 4.49628 11.1129 3.50164C10.4664 2.507 9.51577 1.74796 8.40268 1.3377C7.28958 0.92745 6.0737 0.887957 4.93634 1.22511C3.79897 1.56227 2.80104 2.25801 2.09135 3.20861C1.38166 4.1592 0.99823 5.31371 0.99823 6.5H0Z"
        fill="currentColor"
      />
    </SpinnerSvg>
  </SpinnerWrapper>
);

export default Spinner;

Spinner.displayName = 'Spinner';
